import { useCallback, useEffect, useState } from 'react';

/**
 * Hook for managing active index navigation in dropdown/list components
 * Provides keyboard navigation functionality with bounds checking
 */
export function useActiveIndexNavigation(maxIndex: number = 0) {
    const [activeIndex, setActiveIndex] = useState(0);

    // Ensures active index stays within valid bounds
    const clampIndex = useCallback(
        (index: number) => {
            if (maxIndex < 0) return 0;
            return Math.max(0, Math.min(index, maxIndex));
        },
        [maxIndex]
    );

    // Clamp active index when maxIndex changes (e.g., when options list changes)
    useEffect(() => {
        setActiveIndex(prev => clampIndex(prev));
    }, [maxIndex, clampIndex]);

    const moveActive = useCallback(
        (delta: number) => {
            setActiveIndex(prev => clampIndex(prev + delta));
        },
        [clampIndex]
    );

    const resetActiveIndex = useCallback(() => setActiveIndex(0), []);

    return {
        activeIndex,
        setActiveIndex,
        moveActive,
        resetActiveIndex,
        clampIndex,
    };
}
